import React, { useEffect } from 'react';
import { Icon } from './icons';

export type LandingNavProps = {
  onHome: () => void;
  onFeatures: () => void;
  onSignIn: () => void;
  onGetStarted: () => void;
  active?: 'home' | 'features';
};

/** The Helm logo — a ship's wheel: outer ring, hub, eight spoke handles.
 *  Stroke-drawn so it stays crisp from 15px (nav) to 64px (hero). */
export function HelmMark({ size = 15 }: { size?: number }) {
  const spokes = Array.from({ length: 8 }, (_, i) => {
    const a = (i * Math.PI) / 4;
    const x1 = 12 + 4.6 * Math.sin(a), y1 = 12 - 4.6 * Math.cos(a);
    const x2 = 12 + 11 * Math.sin(a), y2 = 12 - 11 * Math.cos(a);
    return `M${x1.toFixed(2)} ${y1.toFixed(2)}L${x2.toFixed(2)} ${y2.toFixed(2)}`;
  }).join('');
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="2.1" fill="currentColor" stroke="none" />
      <path d={spokes} />
    </svg>
  );
}

/** Small four-point spark — the in-page glyph for "Helm is thinking/suggesting". */
export function Spark({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2c.6 5.4 4.6 9.4 10 10-5.4.6-9.4 4.6-10 10-.6-5.4-4.6-9.4-10-10 5.4-.6 9.4-4.6 10-10Z" />
    </svg>
  );
}

/** Marketing nav — shared by / and /features (new fx-mk namespace). */
export function LandingNav({ onHome, onFeatures, onSignIn, onGetStarted, active }: LandingNavProps) {
  const link = (e: React.MouseEvent, fn: () => void) => { e.preventDefault(); fn(); };
  return (
    <header className="fx-mk-nav">
      <div className="fx-mk-nav-in">
        <a href="/" className="fx-mk-brand" onClick={(e) => link(e, onHome)}>
          <span className="fx-mk-logo"><HelmMark size={16} /></span> Helm
        </a>
        <nav className="fx-mk-links">
          <a href="/" className={active === 'home' ? 'is-active' : ''} onClick={(e) => link(e, onHome)}>Product</a>
          <a href="/features" className={active === 'features' ? 'is-active' : ''} onClick={(e) => link(e, onFeatures)}>Features</a>
        </nav>
        <div className="fx-mk-nav-cta">
          <button type="button" className="fx-mk-ghost-sm" onClick={onSignIn}>Sign in</button>
          <button type="button" className="fx-mk-btn-sm" onClick={onGetStarted}>Get started</button>
        </div>
      </div>
    </header>
  );
}

/** Marketing footer — shared by / and /features. */
export function LandingFooter({ onSignIn, onGetStarted }: { onSignIn: () => void; onGetStarted: () => void }) {
  return (
    <footer className="fx-mk-foot">
      <div className="fx-mk-foot-in">
        <div className="fx-mk-brand"><span className="fx-mk-logo"><HelmMark size={16} /></span> Helm</div>
        <div className="fx-mk-foot-links">
          <button type="button" onClick={onSignIn}>Sign in</button>
          <button type="button" onClick={onGetStarted}>Get started</button>
        </div>
        <div className="fx-mk-foot-c">© Helm — a Future Obvious product</div>
      </div>
    </footer>
  );
}

/* ── Reusable mock fragments (all CSS/SVG, no images) ─────────────────────── */

function HelmCard({ kind, children, actions }: { kind: string; children: React.ReactNode; actions: string[] }) {
  return (
    <div className="fx-mk-helm">
      <div className="fx-mk-helm-h"><Spark size={11} /> Helm · <em>{kind}</em></div>
      <div className="fx-mk-helm-body">{children}</div>
      <div className="fx-mk-helm-a">
        {actions.map((a, i) => <span key={a} className={i === 0 ? 'is-acc' : ''}>{a}</span>)}
      </div>
    </div>
  );
}

/**
 * Product landing page (route: /) — v4, a structural redesign.
 * Architecture: centered editorial hero → full-width product window →
 * capability strip → bento feature grid → statement band → 3 steps → CTA.
 * Committed dark "ink & ember" system, fx-mk-* namespace, zero emoji.
 */
export function Landing(nav: LandingNavProps) {
  const { onGetStarted, onSignIn, onFeatures } = nav;
  // Scroll reveals: sections rise in as they enter the viewport. Reduced-motion
  // users get everything visible immediately (CSS also disables the transition).
  useEffect(() => {
    const els = Array.from(document.querySelectorAll('.fx-mk [data-reveal]'));
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
    }, { threshold: 0.12 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  return (
    <div className="fx-mk">
      <LandingNav {...nav} />

      {/* ── Hero: centered statement, product window below ── */}
      <section className="fx-mk-hero">
        <div className="fx-mk-hero-glow" aria-hidden="true" />
        <div className="fx-mk-chip"><Spark size={11} /> Team chat with a built-in intelligence</div>
        <h1>Your workspace<br /><span className="fx-mk-ember">is the agent.</span></h1>
        <p className="fx-mk-sub">
          Helm is team chat that works back: it drafts the reply, catches the action item,
          finds the doc — and turns everything your team shares into a second brain
          your whole organization can search.
        </p>
        <div className="fx-mk-cta-row">
          <button type="button" className="fx-mk-btn" onClick={onGetStarted}>Get started free</button>
          <button type="button" className="fx-mk-ghost" onClick={onSignIn}>Sign in</button>
        </div>
        <div className="fx-mk-trust">Google &amp; Microsoft SSO · Mandatory 2FA · Your data stays in your workspace</div>

        {/* Full-width product window */}
        <div className="fx-mk-window" aria-hidden="true">
          <div className="fx-mk-winbar"><i /><i /><i /><span className="fx-mk-winurl">Helm — #product</span></div>
          <div className="fx-mk-winbody">
            <div className="fx-mk-w-dock">
              <span className="fx-mk-w-logo"><HelmMark size={13} /></span>
              <span className="fx-mk-w-di is-on" /><span className="fx-mk-w-di" /><span className="fx-mk-w-di" /><span className="fx-mk-w-di" />
            </div>
            <div className="fx-mk-w-side">
              <span className="fx-mk-w-st" />
              <span className="fx-mk-w-row is-on" /><span className="fx-mk-w-row" /><span className="fx-mk-w-row" /><span className="fx-mk-w-row" />
              <span className="fx-mk-w-st" style={{ marginTop: 14 }} />
              <span className="fx-mk-w-row" /><span className="fx-mk-w-row" />
            </div>
            <div className="fx-mk-w-chat">
              <div className="fx-mk-w-head"><span className="fx-mk-w-ct" /><span className="fx-mk-w-cp" /></div>
              <div className="fx-mk-w-msg m1"><span className="fx-mk-w-av" style={{ background: '#7c5cff' }} /><span><i style={{ width: '76%' }} /><i style={{ width: '48%' }} /></span></div>
              <div className="fx-mk-w-msg m2"><span className="fx-mk-w-av" style={{ background: '#2e9e6b' }} /><span><i style={{ width: '58%' }} /></span></div>
              <div className="fx-mk-w-msg m3"><span className="fx-mk-w-av" style={{ background: '#e2703a' }} /><span><i style={{ width: '67%' }} /><i style={{ width: '30%' }} /></span></div>
              <HelmCard kind="Suggested reply" actions={['Send', 'Edit', 'Dismiss']}>
                <i className="fx-mk-hb" style={{ width: '90%' }} /><i className="fx-mk-hb" style={{ width: '64%' }} />
              </HelmCard>
              <div className="fx-mk-w-composer" />
            </div>
          </div>
        </div>
      </section>

      {/* ── Capability strip ── */}
      <div className="fx-mk-strip" role="presentation" data-reveal>
        {['Channels', 'DMs', 'Threads', 'Calls', 'Docs', 'Calendar', 'Tasks', 'Knowledge vault'].map((c) => (
          <span key={c} className="fx-mk-strip-chip">{c}</span>
        ))}
      </div>

      {/* ── Bento grid: the two core concepts + two proofs ── */}
      <section className="fx-mk-bento">
        <div className="fx-mk-card is-wide" data-reveal>
          <div className="fx-mk-card-eyebrow"><Spark size={11} /> The workspace is the agent</div>
          <h2>No bots to configure. The room itself pays attention.</h2>
          <p>
            Helm reads the conversation like a great chief of staff. It drafts the reply you
            were about to write, turns &ldquo;can you handle this by Thursday?&rdquo; into a task on your
            list, and surfaces the document the discussion is actually about.
          </p>
          <div className="fx-mk-card-visual">
            <HelmCard kind="Action item" actions={['Accept', 'Dismiss']}>
              <span className="fx-mk-task"><span className="fx-mk-ring" /> Pull enterprise-tier numbers · by Thursday</span>
            </HelmCard>
            <HelmCard kind="Related doc" actions={['Open', 'Dismiss']}>
              <span className="fx-mk-task"><Icon name="note" size={14} /> Q3 Financial Review — page 4 covers this</span>
            </HelmCard>
          </div>
        </div>

        <div className="fx-mk-card is-tall" data-reveal>
          <div className="fx-mk-card-eyebrow"><Spark size={11} /> A second brain for your organization</div>
          <h2>Every conversation makes the company smarter.</h2>
          <p>
            Files, links, docs, decisions — everything shared becomes living, connected knowledge.
            Ask a question months later; the answer is already there, with its source.
          </p>
          <div className="fx-mk-graph">
            <span className="fx-mk-node is-hub">Q3 launch</span>
            <span className="fx-mk-node g1" style={{ top: '4%', left: '6%' }}>pricing.xlsx</span>
            <span className="fx-mk-node g2" style={{ top: '12%', right: '2%' }}>Board deck</span>
            <span className="fx-mk-node g3" style={{ bottom: '14%', left: '2%' }}>#product thread</span>
            <span className="fx-mk-node g4" style={{ bottom: '2%', right: '8%' }}>Launch checklist</span>
            <svg viewBox="0 0 100 100" preserveAspectRatio="none">
              <line x1="50" y1="50" x2="16" y2="10" /><line x1="50" y1="50" x2="84" y2="18" />
              <line x1="50" y1="50" x2="14" y2="80" /><line x1="50" y1="50" x2="80" y2="92" />
            </svg>
          </div>
        </div>

        <div className="fx-mk-card is-p1" data-reveal>
          <div className="fx-mk-card-ic"><Icon name="markread" size={18} /></div>
          <h3>Human in control</h3>
          <p>Helm suggests; you decide. Every AI action is a private approval — send, edit, or dismiss. Nothing speaks in your name without you.</p>
        </div>

        <div className="fx-mk-card is-p2" data-reveal>
          <div className="fx-mk-card-ic"><Icon name="lock" size={18} /></div>
          <h3>Secure by default</h3>
          <p>Mandatory 2FA, SSO, per-workspace isolation, scoped tokens — and calls on infrastructure you control, not someone else's cloud.</p>
        </div>
      </section>

      {/* ── Statement band ── */}
      <section className="fx-mk-statement" data-reveal>
        <h2>Chat that doesn't <span className="fx-mk-ember">evaporate.</span></h2>
        <p>
          In every other messenger, what your team knows scrolls away forever. In Helm the
          conversation is the input — the searchable, working memory of your company is the output.
        </p>
      </section>

      {/* ── How it works ── */}
      <section className="fx-mk-steps">
        <div className="fx-mk-step" data-reveal>
          <span className="fx-mk-step-n">01</span>
          <h3>Work like you already do</h3>
          <p>Chat, share files, jump on calls, write docs. No new habits, no prompt engineering.</p>
        </div>
        <div className="fx-mk-step" data-reveal>
          <span className="fx-mk-step-n">02</span>
          <h3>The workspace pays attention</h3>
          <p>Helm drafts replies, captures action items, and files every document and link into the vault.</p>
        </div>
        <div className="fx-mk-step" data-reveal>
          <span className="fx-mk-step-n">03</span>
          <h3>Your second brain compounds</h3>
          <p>Decisions stay findable, context survives turnover, answers come with sources.</p>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="fx-mk-final" data-reveal>
        <h2>Give your team a workspace<br />that works back.</h2>
        <div className="fx-mk-cta-row">
          <button type="button" className="fx-mk-btn" onClick={onGetStarted}>Get started free</button>
          <button type="button" className="fx-mk-ghost" onClick={onFeatures}>Explore the features</button>
        </div>
      </section>

      <LandingFooter onSignIn={onSignIn} onGetStarted={onGetStarted} />
    </div>
  );
}
